"use client";

import type { NavActionCtaData } from "@homeinstead/shared/site-builder";
import type React from "react";

import { formatPhoneDisplay } from "@homeinstead/shared";
import { cn } from "../../../utils/classnames";
import { NavBarLogoLink } from "../NavBarLogoLink";
import { NavigationBottomPanel } from "../NavigationBottomPanel";
import { useScrolledPastViewport } from "../use-scrolled-past-viewport";
import { CoBrandedCenterColumn } from "./CoBrandedCenterColumn";
import { CoBrandedPartnerColumn } from "./CoBrandedPartnerColumn";

const DEFAULT_PHONE_ROW_LABEL = "We're open! Call us now: ";

export type NavBarCoBrandedPartnerLogo = {
  alt: string;
  height: number;
  src: string;
  width: number;
};

type NavBarCoBrandedProps = React.ComponentPropsWithoutRef<"nav"> & {
  coBrandPhoneCtaText?: string | null;
  "data-sanity"?: string | null;
  disableLogoLink?: boolean;
  partnerLogo: NavBarCoBrandedPartnerLogo;
  phoneNumber: string;
  /**
   * The secondary CTA default variant. Co-branded headers render the default
   * in the center column on large viewports; additional flag-gated variants
   * are not surfaced in co-branded layouts (they would compete with the
   * partner phone row). Pass null to omit the consultation CTA entirely.
   */
  secondaryCta: NavActionCtaData | null;
  stickyCta?: NavActionCtaData | null;
};

function NavBarCoBranded({
  className,
  coBrandPhoneCtaText,
  "data-sanity": dataSanity,
  disableLogoLink = false,
  partnerLogo,
  phoneNumber,
  secondaryCta,
  stickyCta = null,
  ...props
}: NavBarCoBrandedProps) {
  const scrolledDown = useScrolledPastViewport({
    enabled: secondaryCta !== null || stickyCta !== null,
  });
  const formattedPhone = formatPhoneDisplay(phoneNumber);
  const phoneRowLabel = coBrandPhoneCtaText?.trim() || DEFAULT_PHONE_ROW_LABEL;

  return (
    <>
      <nav
        className={cn(
          "navbar",
          "relative z-40 w-full bg-new-beginnings-base text-support-base shadow-navbar-ppc",
          className,
        )}
        data-sanity={dataSanity ?? undefined}
        {...props}
      >
        <div className="mx-auto flex h-20 w-full max-w-screen-hd items-center justify-between gap-4 p-4">
          <div className="flex min-w-0 shrink-0 items-center py-3 sm:py-4">
            <NavBarLogoLink disableLogoLink={disableLogoLink} />
          </div>

          <CoBrandedCenterColumn
            formattedPhone={formattedPhone}
            phoneNumber={phoneNumber}
            phoneRowLabel={phoneRowLabel}
            scrolledDown={scrolledDown}
            secondaryCta={secondaryCta}
          />

          <CoBrandedPartnerColumn
            partnerLogo={partnerLogo}
            phoneNumber={phoneNumber}
            scrolledDown={scrolledDown}
          />
        </div>
      </nav>
      <NavigationBottomPanel
        phoneNumber={phoneNumber}
        scrolledPastViewport={scrolledDown}
        stickyCta={stickyCta}
      />
    </>
  );
}

export { NavBarCoBranded };
